import React from "react";
import { Hexagon, KeyRound, Cpu, LogOut, Printer, ShieldCheck } from "lucide-react";
import { Dot } from "./ui";
import { LangSwitch } from "./LangSwitch";
import { ThemeSwitch } from "./ThemeSwitch";
import { useI18n, pickLang } from "../lib/i18n";

export default function Topbar({ status, session, health, onOpenApiKey, onSignOut }) {
  const { t, lang } = useI18n();
  const s = status || {};
  const gwOk = s.api_gateway_status === "connected";
  const beOk = health?.status === "ok";
  return (
    <header className="glass border-b border-edge">
      <div className="flex items-center justify-between gap-4 px-5 py-3 lg:px-6">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span className="relative grid shrink-0 place-items-center h-10 w-10 rounded-xl border border-aegis-cyan/30 bg-aegis-cyan/10 text-aegis-cyan">
            <Hexagon size={20} strokeWidth={1.6} />
            <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-aegis-emerald ring-2 ring-void" />
          </span>
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <h1 className="truncate font-heading text-[14px] sm:text-[17px] font-semibold tracking-tight text-slate-100">
                {t("brand.name")}
              </h1>
              <span className="hidden sm:inline-block font-mono text-[10px] text-aegis-cyan border border-aegis-cyan/30 rounded px-1.5 py-0.5">
                {s.version || "v2.0.0"}
              </span>
            </div>
            <p className="hidden sm:block text-[11px] text-slate-400 truncate">{t("brand.tagline")}</p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-3 lg:gap-4">
          <div
            className="hidden 2xl:flex items-center gap-2 rounded-lg border border-aegis-emerald/25 bg-aegis-emerald/10 px-2.5 py-1"
            data-testid="agentic-flag-badge"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-aegis-emerald animate-pulseGlow" />
            <span className="font-mono text-[10px] text-aegis-emerald">AGENTIC_AI_ENABLED=true</span>
          </div>

          <div
            className={`hidden lg:flex items-center gap-2 rounded-lg border px-2.5 py-1 ${
              beOk ? "border-aegis-emerald/25 bg-aegis-emerald/10" : "border-aegis-red/30 bg-aegis-red/10"
            }`}
            data-testid="backend-health"
            title={health?.mongo}
          >
            <span className={`h-1.5 w-1.5 rounded-full animate-pulseGlow ${beOk ? "bg-aegis-emerald" : "bg-aegis-red"}`} />
            <span className={`font-mono text-[10px] ${beOk ? "text-aegis-emerald" : "text-aegis-red"}`}>
              {beOk ? "FastAPI · MongoDB READY" : "BACKEND DEGRADED"}
            </span>
          </div>

          <div className="hidden xl:flex items-center gap-2" data-testid="api-gateway-status">
            <Dot color={gwOk ? "emerald" : "red"} />
            <span className="text-[11px] text-slate-400">API Gateway</span>
          </div>

          <div className="hidden xl:flex items-center gap-2 border-l border-edge pl-4">
            <Cpu size={15} className="text-aegis-purple" />
            <span className="text-[11px] text-slate-300">{s.profile || "AWS Bedrock Profile"}</span>
          </div>

          <ThemeSwitch className="no-print" />
          <LangSwitch className="no-print" />

          {session && (
            <div className="flex items-center gap-2 border-l border-edge pl-3" data-testid="officer-badge">
              <span className="grid place-items-center h-8 w-8 rounded-lg border border-aegis-emerald/30 bg-aegis-emerald/10 text-aegis-emerald">
                <ShieldCheck size={15} />
              </span>
              <div className="hidden md:flex flex-col leading-tight">
                <span className="text-[11px] text-slate-200">{pickLang(session.officer, "name", lang)}</span>
                <span className="font-mono text-[9px] text-slate-400">{session.officer.officer_id}</span>
              </div>
            </div>
          )}

          <button
            onClick={() => window.print()}
            data-testid="print-btn"
            title={t("shell.print")}
            className="no-print hidden sm:grid place-items-center h-9 w-9 rounded-lg border border-edge raise text-slate-300 hover:text-aegis-cyan hover:border-aegis-cyan/40 transition-colors duration-200"
          >
            <Printer size={16} />
          </button>

          <button
            onClick={onOpenApiKey}
            data-testid="open-apikey-btn"
            className="no-print hidden sm:grid place-items-center h-9 w-9 rounded-lg border border-edge raise text-slate-300 hover:text-aegis-purple hover:border-aegis-purple/40 transition-colors duration-200"
            title="Configure inference key"
          >
            <KeyRound size={16} />
          </button>

          {session && (
            <button
              onClick={onSignOut}
              data-testid="signout-btn"
              title={t("shell.signout")}
              className="no-print grid place-items-center h-9 w-9 rounded-lg border border-edge raise text-slate-300 hover:text-aegis-red hover:border-aegis-red/40 transition-colors duration-200"
            >
              <LogOut size={16} />
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
